import React, { useState } from "react";
import { BREAKPOINT } from "utils/styles";
import styled from "styled-components";
import {
  ToggleButton as BSToggleButton,
  ToggleButtonGroup as BSToggleButtonGroup,
} from "react-bootstrap";
import { getFilenamePrefix } from "utils/files";
import AudienceEmoji from "./AudienceEmoji";
import { AUDIENCE_TYPES } from "posts/constants";

const spiderData = [
  {
    mode: "youTubeChannels",
    title: "YouTube",
    svg: "/images/spider-graphs/youtubechannels_augustupdate.svg",
    alt: "A word spider depicting top Youtube channels.",
  },
  {
    mode: "youTubeMusic",
    title: "Music",
    svg: "/images/spider-graphs/youtubemusic_augustupdate.svg",
    alt: "A word spider depicting top Youtube Music channels.",
  },
  {
    mode: "news",
    title: "News",
    svg: "/images/spider-graphs/news_augustupdate.svg",
    alt: "A word spider depicting top News channels.",
  },
  {
    mode: "netflix",
    title: "Netflix",
    svg: "/images/spider-graphs/netflix_augustupdate.svg",
    alt: "A word spider depicting top Netflix shows or movies.",
  },
];

const WordSpiderContainer = styled.div`
  max-width: 60rem;
  margin: 0 auto;
  small {
    display: block;
    margin: auto;
    max-width: 400px;
  }
`;

const WordSpiderGraph = styled.div`
  width: 100%;
  position: relative;
`;

const Image = styled.img`
  width: 100%;
`;

const ButtonContainer = styled.div`
  width: 100%;
  margin-bottom: 0rem;
`;

const ToggleButtonGroup = styled(BSToggleButtonGroup)`
  background: #f2f2f2;
  padding: 0.5rem;
  border-radius: 25px;
  margin-top: 2rem;
`;
const ToggleButton = styled(BSToggleButton)`
  background: #f2f2f2;
  max-width: 200px;
  margin-right: 1rem;
  border-radius: 15px !important;
  box-shadow: none !important;
  border: none !important;

  &:last-child {
    margin-right: 0;
  }

  &.active {
    background-color: #fff !important;
    font-family: "mabry-medium", sans-serif !important;
    font-weight: 500 !important;
  }
`;

const AudienceText = styled.div.attrs({ className: "mabry-medium" })`
  position: absolute;
`;

const AudienceAxis = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;

  ${AudienceText} {
    &:nth-child(1) {
      top: 4rem;
      left: 4.2rem;
      @media ${BREAKPOINT.M} {
        font-size: 0.7rem;
        top: 30px;
        left: 35px;
      }
    }
    &:nth-child(2) {
      top: 4rem;
      right: 4.2rem;
      @media ${BREAKPOINT.M} {
        font-size: 0.7rem;
        top: 30px;
        right: 35px;
      }
    }
    &:nth-child(3) {
      bottom: 4rem;
      left: 4.2rem;
      @media ${BREAKPOINT.M} {
        font-size: 0.7rem;
        bottom: 30px;
        left: 35px;
      }
    }
    &:nth-child(4) {
      bottom: 4rem;
      right: 4.2rem;
      @media ${BREAKPOINT.M} {
        font-size: 0.7rem;
        bottom: 30px;
        right: 35px;
      }
    }
  }
`;

export default function NarrativesTimelineGraph() {
  const [graphIndex, setGraphIndex] = useState(0);

  const renderToggleButtons = () => {
    return (
      <ToggleButtonGroup
        name="audience-compare-button-group"
        type="radio"
        value={graphIndex}
      >
        {spiderData.map((spider, i) => (
          <ToggleButton
            key={spider.title}
            value={i}
            variant="light"
            onClick={() => {
              setGraphIndex(i);
            }}
          >
            {spider.title}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    );
  };

  const renderImage = (src, alt) => {
    const imgSrcPrefix = getFilenamePrefix(src);
    return (
      <picture>
        <source srcSet={`${imgSrcPrefix}.svg`} type="image/svg" />
        <Image alt={alt} loading="eager" src={src} />
      </picture>
    );
  };

  const renderCurrentGraph = () => {
    let { svg, alt } = spiderData[graphIndex];
    return renderImage(svg, alt);
  };

  return (
    <WordSpiderContainer>
      <ButtonContainer>{renderToggleButtons()}</ButtonContainer>
      <WordSpiderGraph>
        {renderCurrentGraph()}{" "}
        <AudienceAxis>
          <AudienceText>
            <AudienceEmoji audience={AUDIENCE_TYPES.PEOPLE_POWER} />
            People Power
          </AudienceText>
          <AudienceText>
            If You Say So{"  "}
            <AudienceEmoji
              noMarginRight
              audience={AUDIENCE_TYPES.IF_YOU_SAY_SO}
            />
          </AudienceText>
          <AudienceText>
            <AudienceEmoji audience={AUDIENCE_TYPES.TOUGH_COOKIES} />
            Tough Cookies
          </AudienceText>
          <AudienceText>
            Don't Tread on Me{"  "}
            <AudienceEmoji
              noMarginRight
              audience={AUDIENCE_TYPES.DONT_TREAD_ON_ME}
            />
          </AudienceText>
        </AudienceAxis>
      </WordSpiderGraph>

      <small>
        Bigger words in the chart are more common overall. Words that appear in
        the center of a quadrant 'belong' to that group. Words close to an axis
        are shared by two groups.
      </small>
    </WordSpiderContainer>
  );
}
